import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import type {
    ILegacyIndustryFilterDto,
    IndustryValueItemDto,
} from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import type { ILeadsIndustryFilterOptionsDto } from "app/pages/sales-intelligence/domain/leads/types/filter-options";
import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterPanelBaseFilterDeps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";
import type { FilterValueDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import { calculateArrayDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/calculateValueDeltaUtils";
import { LegacyIndustryFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/models/LegacyIndustryFilter/LegacyIndustryFilterValue";
import type { AdvancedSearchSettingsService } from "app/pages/sales-intelligence/sub-modules/advanced-search/services/sw-settings/types";
import type { SuggestedIndustriesMap } from "app/pages/sales-intelligence/types";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import type { LegacyIndustryCategoriesById, LegacyIndustryFilterValueType } from "./types";

type LegacyIndustryFilterOptions = Pick<ILeadsIndustryFilterOptionsDto, "industry" | "industrySuggestions">;

/**
 * @deprecated The legacy 2-level industry filter (wire key `industryFilter`). Superseded by the
 * taxonomy-backed `IndustryTaxonomyFilterModel` (SI-8780). Kept intact so existing saved searches, scoring
 * rules and team overlays keep working; delete this folder when v1 is sunset.
 */
class LegacyIndustryFilterModel extends FilterPanelBaseFilter<
    ILegacyIndustryFilterDto,
    LegacyIndustryFilterOptions,
    LegacyIndustryFilterValueType
> {
    readonly types: readonly InclusionOption[];
    readonly industries: readonly IFlattenedCategory[];
    readonly categoriesById: LegacyIndustryCategoriesById;
    readonly suggestedIndustries: SuggestedIndustriesMap;

    constructor(
        deps: IFilterPanelBaseFilterDeps<ILegacyIndustryFilterDto, LegacyIndustryFilterOptions>,
        settingsService: AdvancedSearchSettingsService,
    ) {
        super(deps);

        const {
            options: { industry, industrySuggestions },
        } = deps;

        const categories = settingsService
            .getCurrentComponentIndustries(industry.industries)
            .filter((c) => c.id !== "All");

        this.types = industry.inclusionOptions;

        this.industries = categories;
        this.categoriesById = categories.reduce<{ [id: string]: IFlattenedCategory }>((map, category) => {
            map[category.id] = category;

            return map;
        }, {});
        this.suggestedIndustries = this.prependParentIndustry(industrySuggestions);
    }

    private withParentIndustry(subIndustryId: string): string {
        return (
            Object.keys(this.categoriesById).find((id) => {
                if (!id.includes("~")) {
                    return false;
                }
                return id.split("~")[1] === subIndustryId;
            }) || ""
        );
    }

    private prependParentIndustry(suggestions: SuggestedIndustriesMap): SuggestedIndustriesMap {
        return Object.keys(suggestions).reduce<SuggestedIndustriesMap>((result, subIndustryId) => {
            const subIndustryIdWithParent = this.withParentIndustry(subIndustryId);
            result[subIndustryIdWithParent] = suggestions[subIndustryId].map((suggestion) => ({
                ...suggestion,
                suggestedSubIndustry: this.withParentIndustry(suggestion.suggestedSubIndustry),
            }));

            return result;
        }, {});
    }

    private flattenCategories(values: readonly IndustryValueItemDto[]) {
        return values.map((id) => {
            const { mainCategory = "", subCategory } = id;
            return `${mainCategory}${subCategory ? `~${subCategory}` : ""}`;
        });
    }

    fromApiValue(value?: ILegacyIndustryFilterDto): LegacyIndustryFilterValueType {
        const filterValue = new LegacyIndustryFilterValue(value);

        return {
            selectedIndustries: this.flattenCategories(filterValue.values),
            inclusion: filterValue.inclusion,
        };
    }

    toApiValue({
        selectedIndustries,
        ...baseValues
    }: LegacyIndustryFilterValueType): ILegacyIndustryFilterDto | undefined {
        if (!selectedIndustries.length) {
            return undefined;
        }

        const values = selectedIndustries.map((industry) => {
            const [mainCategory, subCategory] = industry.split("~");
            return { mainCategory, subCategory };
        });

        return {
            ...baseValues,
            values,
        };
    }

    getEmptyValue(): ILegacyIndustryFilterDto | undefined {
        return undefined;
    }

    calculateValueDelta = (
        previousValue: LegacyIndustryFilterValueType | undefined,
        newValue: LegacyIndustryFilterValueType | undefined,
    ): FilterValueDelta => {
        return calculateArrayDelta(previousValue?.selectedIndustries, newValue?.selectedIndustries);
    };

    isValueEmpty(value: ILegacyIndustryFilterDto): boolean {
        return new LegacyIndustryFilterValue(value).isEmpty();
    }
}

export default LegacyIndustryFilterModel;
